import { 
  Box, 
  CircularProgress, 
  IconButton, 
  Button, 
  Alert
} from '@mui/material';
import { ChevronLeft, ChevronRight, Settings } from '@mui/icons-material';
import React, { useContext, useEffect, useState } from 'react';
import Grid2 from '@mui/material/Unstable_Grid2';
import { AuthContext } from '../../../../contexts/user-context/user-context';
import axios from 'axios';
import { API_ENDPOINT } from '../../../../configurations/global.config';
import { useSnackBar } from '../../../../contexts/snackbar';
import { IconWithText } from '../../../common/styles/components';
import { WoocommerceIntegrationConfig } from '../../../../interfaces/common/woocommerce';
import WoocommerceIntegration from './woocommerce-integration';
import WoocommerceIntegrationConfigForm from './integration-config';
import WoocommerceOrders from './woocommerce-orders';
import NewFeatureChip from '../../../common/new-feature-chip';
import { IntroDialog } from './intro-dialog';

const WoocommerceDashboardSection = ({style, children}: {style?: React.CSSProperties, children: React.ReactNode}) => {
  return (
    <Box 
      maxWidth={"650px"} 
      width={"100%"}
      border={"1px solid #ddd"}
      borderRadius={6}
      sx={{
        paddingRight: 4, 
        paddingLeft: 4, 
        paddingTop: 2, 
        paddingBottom: 2, 
        ...(style??{}),
        transition: "all 0.3s ease"
      }}
      display={"flex"}
      flexDirection={"column"}
      gap={2}
      alignItems={"center"}
    >
      {children}
    </Box>
  );
};

function WoocommerceDashboard() {
  const { config } = useContext(AuthContext);
  const { showMessage } = useSnackBar();
  const [connectedShop, setConnectedShop] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [woocommerceConfig, setWoocommerceConfig] = useState<WoocommerceIntegrationConfig | null>(null);
  const [storeId,setStoreId] = useState<number | null>(null);
  const [showIntro, setShowIntro] = useState(false)
  const [finishingIntro, setFinishingIntro] = useState(false)

  // Sidebar state
  const [isSidebarOpen, setIsSidebarOpen] = useState<boolean>(() => {
    // Initialize from localStorage on first render
    const saved = localStorage.getItem("PT_WOOCOMMERCE_SIDEBAR_OPEN");
    return saved !== null ? JSON.parse(saved) : true; // default: open
  });

  // Persist state changes
  useEffect(() => {
    localStorage.setItem("PT_WOOCOMMERCE_SIDEBAR_OPEN", JSON.stringify(isSidebarOpen));
  }, [isSidebarOpen]);

  useEffect(()=>{
    if (!connectedShop) {
      setStoreId(null)
      setWoocommerceConfig(null)
    }
  }, [connectedShop])

  useEffect(() => {
    const fetchIntegration = async () => {
      setIsLoading(true);
      try {
        const response = await axios.get<WoocommerceIntegrationConfig>(
          `${API_ENDPOINT}/woocommerce/v1/integration/`,
          config
        );
        setConnectedShop(response.data.shop_domain);
        if (response.data.packaging_mode) {
          setWoocommerceConfig(response.data);
        }
        if (response.data.id) {
          setStoreId(response.data.id);
        }
      } catch (error: any) {
        if (error.response?.status === 404) {
          setConnectedShop(null);
        } else {
          showMessage('Failed to load Woocommerce integration', 'error');
        }
      } finally {
        setIsLoading(false);
      }
    };
    fetchIntegration();
  }, [connectedShop]);

  return (
    <>
      {isLoading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight={150}>
          <CircularProgress />
        </Box>
      ) : (
        <>

        <Box maxWidth={600} mb={2}>
         <NewFeatureChip label='Beta' message='Woocommerce integration is in beta phase! Try it out and give us your feedback!' />
        </Box>

        
        {
          !isSidebarOpen && (
            <Box>
              <IconWithText onClick={() => setIsSidebarOpen(!isSidebarOpen)} >
                <ChevronRight /> Show Integration Details
              </IconWithText>
            </Box>
          )
        }

        <Grid2 container spacing={2} width={"100%"}>
          {/* LEFT SIDEBAR */}
          <Grid2 xs={12} position="relative" md={isSidebarOpen ? 4 : 0} sx={{transition: "all 0.3s ease"}}>
            {isSidebarOpen && woocommerceConfig && <Box sx={{position: "absolute", top: 15, right: 25, zIndex: 100, whiteSpace: "nowrap"}}>
              <IconWithText onClick={() => setIsSidebarOpen(!isSidebarOpen)} >
                <ChevronLeft /> Hide
              </IconWithText>
            </Box>}
              
            {isSidebarOpen && (
              <Box 
                width={"100%"}
                display={"flex"} 
                flexDirection={"column"} 
                gap={2} 
                alignItems={"flex-start"}
              >
                <WoocommerceDashboardSection>
                  {isLoading ? <CircularProgress /> : <WoocommerceIntegration connectedShop={connectedShop} setConnectedShop={setConnectedShop} />}

                {storeId && woocommerceConfig && (
                    <WoocommerceIntegrationConfigForm
                      value={woocommerceConfig}
                      storeId={storeId}
                      updateValue={setWoocommerceConfig}
                    />
                )}
                </WoocommerceDashboardSection>
              </Box>
            )}
          </Grid2>


          {/* RIGHT CONTENT */}
          {woocommerceConfig && <Grid2 xs={12} md={isSidebarOpen ? 8 : 12} position="relative">
            <WoocommerceDashboardSection style={{maxWidth: "100%"}}>
              <WoocommerceOrders integrationConfig={woocommerceConfig} /> :
            </WoocommerceDashboardSection>
          </Grid2>}

          {!woocommerceConfig && <Grid2 xs={12} md={isSidebarOpen ? 8 : 12} position="relative" >
              <Box
                component="img"
                src={"/woocommerce-intro.png"}
                sx={{
                  width: "100%",
                  objectFit: "contain",
                  display: "inline-block",
                  verticalAlign: "middle",
                  borderRadius: 5,
                }}
              />

              <Box position={"absolute"} top={0} height={"100%"} width={"100%"} display={"flex"} justifyContent={"center"} alignItems={"center"}>

              <Button
                variant="contained"
                onClick={()=>setShowIntro(true)}
                sx={{
                  background: "linear-gradient(135deg, #FF6EC4 0%, #7873F5 100%)",
                  color: "white",
                  fontWeight: "bold",
                  fontSize: "1.1rem",
                  padding: "12px 32px",
                  borderRadius: "30px",
                  boxShadow: "0 4px 20px rgba(120, 115, 245, 0.4)",
                  textTransform: "none",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    background: "linear-gradient(135deg, #FF85E1 0%, #918CFF 100%)",
                    transform: "scale(1.05)",
                    boxShadow: "0 6px 25px rgba(120, 115, 245, 0.6)",
                  },
                }}
              >
                Get started
              </Button>

              </Box>
            </Grid2>}
        </Grid2>

        {showIntro && <IntroDialog 
          onClose={()=>setShowIntro(false)} 
          connectedShop={connectedShop} 
          setConnectedShop={setConnectedShop} 
          storeConfig={woocommerceConfig}
          setStoreConfig={setWoocommerceConfig}
          storeId={storeId}
        />}

        </>
      )}
    </>
  );
}

export default WoocommerceDashboard;
